import { useEffect, useState } from 'react';

import { expVal } from '@confluence/feature-experiments';
import type { ConfluenceEdition } from '@confluence/session-data';

import { useFeatureExposedAnalytics } from './useFeatureExposedAnalytics';
import { useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment } from './useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment';

export const useUpflowV2ConfluenceStandardFGsExperiment = ({
	skip,
	source,
	currentEdition,
	targetEdition,
}: {
	skip: boolean;
	source: string;
	currentEdition: ConfluenceEdition | null;
	targetEdition: ConfluenceEdition | null;
}) => {
	const fireExperimentExposureEvent = useFeatureExposedAnalytics();
	const [abCohort, setAbCohort] = useState<string | null>(null);

	const {
		isEligible,
		isSiteAdmin,
		isBillingAdmin,
		error: eligibilityHookError,
		loading: eligibilityHookLoading,
	} = useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment({
		skip,
		currentEdition,
		targetEdition,
	});

	useEffect(() => {
		if (isEligible) {
			// AA
			fireExperimentExposureEvent({
				flagKey: 'grw-1930_confluence_upv2_standard_aa',
				cohort: expVal('grw-1930_confluence_upv2_standard_aa', 'cohort', 'not-enrolled'),
				growthInitiative: 'luna',
				source,
				additionalAttributes: {
					currentEdition,
					targetEdition,
					isSiteAdmin,
					isBillingAdmin,
				},
			});
			// AB
			fireExperimentExposureEvent({
				flagKey: 'grw-1930_confluence_upv2_standard_ab',
				cohort: expVal('grw-1930_confluence_upv2_standard_ab', 'cohort', 'not-enrolled'),
				growthInitiative: 'luna',
				source,
				additionalAttributes: {
					currentEdition,
					targetEdition,
					isSiteAdmin,
					isBillingAdmin,
				},
			});

			setAbCohort(expVal('grw-1930_confluence_upv2_standard_ab', 'cohort', 'not-enrolled'));
		} else if (eligibilityHookError) {
			// Error occurred, reset to null
			setAbCohort(null);
		} else {
			setAbCohort('not-enrolled');
		}
	}, [
		fireExperimentExposureEvent,
		isEligible,
		eligibilityHookError,
		source,
		currentEdition,
		targetEdition,
		isSiteAdmin,
		isBillingAdmin,
	]);

	return {
		cohort: abCohort,
		isSiteAdmin,
		isBillingAdmin,
		loading: eligibilityHookLoading,
		error: eligibilityHookError,
	};
};
